import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BrandGroup as BrandGroupType,
  TopCategoriesModule
} from 'types/capi/modules';
import type { ExternalLink, InternalLink } from 'types/capi/shared';

import HorizontalLine from '~/components/HorizontalLine';
import Tracked from '~/components/Tracked';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';

type Props = {
  categories: TopCategoriesModule;
  modulePosition: number;
};

export default function TopCategories({ categories, modulePosition }: Props) {
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-white">
      <div className="content-width flex flex-col items-center gap-6 py-10 md:py-20">
        <div className="flex flex-col items-center gap-6 self-stretch md:overflow-hidden">
          <h2 className="text-title-xl text-center">{t('topCategories')}</h2>
          <p className="max-w-xl text-justify">{categories.text}</p>
          <div className="mt-7 flex w-full flex-col items-center gap-4 md:gap-6">
            <BrandGroups
              brands={categories.brand_groups}
              modulePosition={modulePosition}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// The module is one rank space: `position` runs across every group in render order, so the
// third link overall is 3 whichever column it sat in. Which group a link belonged to is read off
// its destination downstream, not carried.
function BrandGroups({
  brands,
  modulePosition
}: {
  brands: BrandGroupType[];
  modulePosition: number;
}) {
  let offset = 0;

  return (
    <div className="flex w-full flex-col justify-center gap-4 md:max-w-[800px] md:flex-row md:gap-6">
      {brands.map((brand, index) => {
        const firstPosition = offset + 1;
        offset += brand.items.length;

        return (
          <React.Fragment key={`brand-group-${brand.items[0]?.text}-${index}`}>
            <BrandGroup
              firstPosition={firstPosition}
              items={brand.items}
              modulePosition={modulePosition}
            />
            <HorizontalLine className="col-span-full md:hidden" />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BrandGroup({
  firstPosition,
  items,
  modulePosition
}: {
  firstPosition: number;
  items: BrandGroupType['items'];
  modulePosition: number;
}) {
  return (
    <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-1 md:gap-5">
      {items.map((item, index) => (
        <Item
          key={item.text}
          boldText={index === 0}
          item={item}
          modulePosition={modulePosition}
          position={firstPosition + index}
        />
      ))}
    </div>
  );
}

// An external item (none is authored today) is a click-out, not a navigation_link, so only the
// internal branch fires.
function Item({
  item,
  boldText,
  modulePosition,
  position
}: {
  boldText?: boolean;
  item: InternalLink | ExternalLink;
  modulePosition: number;
  position: number;
}) {
  const fireNavigationLink = useNavigationLinkInteraction('module');

  return (
    <div
      className={`bg-grey-tile p-1 text-center ${boldText ? 'font-semibold' : ''}`}
    >
      {item.type === 'internal' ? (
        <Tracked.InternalLink
          variant="none"
          to={item.path}
          name="TOP_CATEGORY"
          pagePosition="Homepage Top Categories"
          onClick={() =>
            fireNavigationLink(item.path, position, {
              label: item.text,
              modulePosition,
              moduleType: 'homepage-top-categories'
            })
          }
        >
          {item.text}
        </Tracked.InternalLink>
      ) : (
        <Tracked.ExternalLink
          variant="none"
          href={item.url}
          name="TOP_CATEGORY"
          pagePosition="Homepage Top Categories"
        >
          {item.text}
        </Tracked.ExternalLink>
      )}
    </div>
  );
}
